import { useEffect, useState } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator } from 'react-native';
import Animated, { FadeInDown, FadeIn } from 'react-native-reanimated';
import { ArrowLeft, ShieldCheck, Mail, KeyRound, Linkedin } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { handleError } from '@/lib/errors';
import { claimRefusalMessage } from '@/lib/company/claim-refusals';
import {
  requestEmailCode,
  submitEmailCode,
  requestLinkedInProof,
} from '@/lib/supabase/claim-verification';
import { useClaimCompany } from '@/lib/supabase/company-directory';
import { supabase } from '@/lib/supabase/client';
import AnimatedPressable from '@/components/AnimatedPressable';

interface ClaimVerificationPanelProps {
  directoryId: string;
  companyName: string;
  userId: string;
  onCancel: () => void;
  onClaimComplete: (result: { profileId: string; directoryId: string }) => void;
}

type Method = 'email' | 'linkedin';
type Stage = 'method-select' | 'email' | 'code' | 'linkedin' | 'claiming';

/**
 * Two verification methods (Phase 116.2 + 116-2.1):
 *
 *   email — corporate-domain 6-digit OTP. Default for founders with a
 *     company email at the directory row's extracted domain.
 *
 *   linkedin — degraded "employee proof" via the founder's LinkedIn
 *     OIDC identity + LinkdAPI scrape. One-shot. Covers founders on
 *     generic email domains whose LinkedIn profile lists the target
 *     company as a current or past role. Requires prior sign-in via
 *     `linkedin_oidc`.
 *
 * After either method flips a verification row to `verified`,
 * `useClaimCompany.mutate` fires with the `verificationId`, and the claim is
 * made by `onboarding-claim { directoryId }` on the server (118-10) — a
 * failure is shown here and `onClaimComplete` is not called.
 */
export function ClaimVerificationPanel({
  directoryId,
  companyName,
  userId,
  onCancel,
  onClaimComplete,
}: ClaimVerificationPanelProps) {
  const colors = useThemeColors();
  const [stage, setStage] = useState<Stage>('method-select');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const claimMutation = useClaimCompany();

  // Phase 116-2.1 — detect whether the founder is signed in via LinkedIn.
  // The store's UserInfo is a UI projection and doesn't carry provider info,
  // so query Supabase directly. Async + cached in state.
  const [hasLinkedInIdentity, setHasLinkedInIdentity] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const linked = data.user?.identities?.some(
        (i) => i.provider === 'linkedin_oidc',
      ) ?? false;
      if (!cancelled) setHasLinkedInIdentity(linked);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectMethod = (m: Method) => {
    setErrorMessage(null);
    setStage(m === 'email' ? 'email' : 'linkedin');
  };

  const handleLinkedInProof = async () => {
    if (busy) return;
    setErrorMessage(null);
    setBusy(true);
    try {
      const res = await requestLinkedInProof({ directoryId });
      setVerificationId(res.verificationId);
      setStage('claiming');
      const result = await claimMutation.mutateAsync({
        directoryId,
        userId,
        verificationId: res.verificationId,
      });
      onClaimComplete(result);
    } catch (err) {
      // A closed company (or an erasure in flight) is an answer, not a
      // failure: show its words and report nothing.
      const refused = claimRefusalMessage(err);
      if (refused) {
        setErrorMessage(refused);
      } else {
        const { message } = handleError(err, {
          context: 'ClaimVerificationPanel.linkedinProof',
        });
        setErrorMessage(message);
      }
      // Step back so the user can retry or pick email instead.
      if (stage === 'claiming') setStage('linkedin');
    } finally {
      setBusy(false);
    }
  };

  const handleRequestCode = async () => {
    if (busy) return;
    setErrorMessage(null);
    if (!email.trim() || !email.includes('@')) {
      setErrorMessage('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const res = await requestEmailCode({
        directoryId,
        email: email.trim(),
      });
      setVerificationId(res.verificationId);
      setStage('code');
    } catch (err) {
      // A closed company (or an erasure in flight) is an answer, not a
      // failure: show its words and report nothing.
      const refused = claimRefusalMessage(err);
      if (refused) {
        setErrorMessage(refused);
      } else {
        const { message } = handleError(err, {
          context: 'ClaimVerificationPanel.requestCode',
        });
        setErrorMessage(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitCode = async () => {
    if (busy || !verificationId) return;
    setErrorMessage(null);
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setErrorMessage('The code is 6 digits.');
      return;
    }
    setBusy(true);
    try {
      await submitEmailCode({ verificationId, code: trimmed });
      setStage('claiming');
      const result = await claimMutation.mutateAsync({
        directoryId,
        userId,
        verificationId,
      });
      onClaimComplete(result);
    } catch (err) {
      // A closed company (or an erasure in flight) is an answer, not a
      // failure: show its words and report nothing.
      const refused = claimRefusalMessage(err);
      if (refused) {
        setErrorMessage(refused);
      } else {
        const { message } = handleError(err, {
          context: 'ClaimVerificationPanel.submitCode',
        });
        setErrorMessage(message);
      }
      // Drop back to code stage if the claim itself failed but verification
      // succeeded (e.g. UNIQUE(user_id) on startup_profiles).
      if (stage === 'claiming') setStage('code');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setVerificationId(null);
    setCode('');
    setStage('email');
    setErrorMessage(null);
  };

  // Within the panel, the back button rewinds the funnel: from a
  // method-specific stage back to the method picker; from the picker
  // back to the parent's search results.
  const handleBack = () => {
    if (stage === 'method-select') {
      onCancel();
      return;
    }
    setErrorMessage(null);
    setCode('');
    setVerificationId(null);
    setStage('method-select');
  };

  return (
    <Animated.View entering={FadeInDown.duration(300)} className="gap-4">
      <Pressable
        onPress={handleBack}
        className="flex-row items-center gap-2 self-start"
      >
        <ArrowLeft size={16} color={colors.primary[600]} />
        <Text style={{ color: colors.primary[600] }} className="font-medium">
          {stage === 'method-select' ? 'Back to search' : 'Choose another method'}
        </Text>
      </Pressable>

      <View
        className="p-5 rounded-2xl"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        <View className="flex-row items-start gap-3 mb-3">
          <View
            className="w-10 h-10 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <ShieldCheck size={20} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text
              style={{ color: colors.text.primary }}
              className="text-lg font-bold"
              numberOfLines={2}
            >
              Verify you own {companyName}
            </Text>
            <Text
              style={{ color: colors.text.secondary }}
              className="text-sm mt-0.5"
            >
              We'll send a 6-digit code to your company email.
            </Text>
          </View>
        </View>

        {stage === 'method-select' && (
          <Animated.View entering={FadeIn.duration(200)} className="gap-3 mt-2">
            <Text style={{ color: colors.text.secondary }} className="text-sm">
              Pick a way to prove you own this company:
            </Text>

            <AnimatedPressable
              onPress={() => handleSelectMethod('email')}
              className="flex-row items-center gap-3 rounded-xl p-4"
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Mail size={20} color={colors.primary[600]} />
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Verify with company email
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                  Use an email at the company's domain. 6-digit code, 15-min lifetime.
                </Text>
              </View>
            </AnimatedPressable>

            <AnimatedPressable
              onPress={() => handleSelectMethod('linkedin')}
              className="flex-row items-center gap-3 rounded-xl p-4"
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <Linkedin size={20} color="#0A66C2" />
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-semibold">
                  Verify with LinkedIn
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                  {hasLinkedInIdentity
                    ? 'Match a current or past role on your LinkedIn profile.'
                    : 'Requires signing in with LinkedIn first.'}
                </Text>
              </View>
            </AnimatedPressable>
          </Animated.View>
        )}

        {stage === 'linkedin' && (
          <Animated.View entering={FadeIn.duration(200)} className="gap-3 mt-2">
            {hasLinkedInIdentity ? (
              <>
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  We'll check whether your LinkedIn profile lists{' '}
                  <Text style={{ color: colors.text.primary }} className="font-semibold">
                    {companyName}
                  </Text>{' '}
                  as a current or past role. No data is changed until verification passes.
                </Text>
                <AnimatedPressable
                  onPress={handleLinkedInProof}
                  disabled={busy}
                  className="rounded-xl py-3 items-center flex-row justify-center gap-2"
                  style={{ backgroundColor: busy ? colors.surface.secondary : '#0A66C2' }}
                >
                  {busy ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <>
                      <Linkedin size={18} color="white" />
                      <Text className="text-white font-semibold">
                        Verify with my LinkedIn profile
                      </Text>
                    </>
                  )}
                </AnimatedPressable>
              </>
            ) : (
              <>
                <Text style={{ color: colors.text.secondary }} className="text-sm">
                  To verify via LinkedIn you need to sign in with LinkedIn first.
                  Go back and use email verification, or sign out and sign back in
                  via LinkedIn from the login screen.
                </Text>
                <AnimatedPressable
                  onPress={() => handleSelectMethod('email')}
                  className="rounded-xl py-3 items-center"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Text className="text-white font-semibold">
                    Switch to email verification
                  </Text>
                </AnimatedPressable>
              </>
            )}
          </Animated.View>
        )}

        {stage === 'email' && (
          <Animated.View entering={FadeIn.duration(200)} className="gap-3 mt-2">
            <View
              className="flex-row items-center rounded-xl px-3"
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: errorMessage ? colors.red.main : colors.border.light,
              }}
            >
              <Mail size={18} color={colors.text.tertiary} />
              <TextInput
                style={{ color: colors.text.primary }}
                className="flex-1 py-3 pl-2.5 text-base"
                placeholder="you@company.com"
                placeholderTextColor={colors.text.tertiary}
                value={email}
                onChangeText={(t) => {
                  setEmail(t);
                  if (errorMessage) setErrorMessage(null);
                }}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                editable={!busy}
                onSubmitEditing={handleRequestCode}
              />
            </View>
            <AnimatedPressable
              onPress={handleRequestCode}
              disabled={busy || !email.trim()}
              className="rounded-xl py-3 items-center"
              style={{
                backgroundColor:
                  busy || !email.trim()
                    ? colors.surface.secondary
                    : colors.primary[600],
              }}
            >
              {busy ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text
                  style={{
                    color: !email.trim() ? colors.text.tertiary : 'white',
                  }}
                  className="font-semibold"
                >
                  Send verification code
                </Text>
              )}
            </AnimatedPressable>
          </Animated.View>
        )}

        {stage === 'code' && (
          <Animated.View entering={FadeIn.duration(200)} className="gap-3 mt-2">
            <Text style={{ color: colors.text.secondary }} className="text-sm">
              We sent a 6-digit code to{' '}
              <Text style={{ color: colors.text.primary }} className="font-semibold">
                {email}
              </Text>
              . It expires in 15 minutes.
            </Text>
            <View
              className="flex-row items-center rounded-xl px-3"
              style={{
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: errorMessage ? colors.red.main : colors.border.light,
              }}
            >
              <KeyRound size={18} color={colors.text.tertiary} />
              <TextInput
                style={{
                  color: colors.text.primary,
                  letterSpacing: 4,
                  fontWeight: '700',
                }}
                className="flex-1 py-3 pl-2.5 text-lg"
                placeholder="123456"
                placeholderTextColor={colors.text.tertiary}
                value={code}
                onChangeText={(t) => {
                  setCode(t.replace(/\D/g, '').slice(0, 6));
                  if (errorMessage) setErrorMessage(null);
                }}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="number-pad"
                editable={!busy}
                onSubmitEditing={handleSubmitCode}
                maxLength={6}
              />
            </View>
            <AnimatedPressable
              onPress={handleSubmitCode}
              disabled={busy || code.length !== 6}
              className="rounded-xl py-3 items-center"
              style={{
                backgroundColor:
                  busy || code.length !== 6
                    ? colors.surface.secondary
                    : colors.primary[600],
              }}
            >
              {busy ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text
                  style={{
                    color: code.length !== 6 ? colors.text.tertiary : 'white',
                  }}
                  className="font-semibold"
                >
                  Verify and claim
                </Text>
              )}
            </AnimatedPressable>
            <Pressable onPress={handleResend} className="items-center py-1">
              <Text
                style={{ color: colors.primary[600] }}
                className="text-sm font-medium"
              >
                Use a different email
              </Text>
            </Pressable>
          </Animated.View>
        )}

        {stage === 'claiming' && (
          <Animated.View
            entering={FadeIn.duration(200)}
            className="flex-row items-center gap-3 mt-2"
          >
            <ActivityIndicator size="small" color={colors.primary[600]} />
            <Text style={{ color: colors.text.secondary }} className="text-sm">
              Linking your account to {companyName}…
            </Text>
          </Animated.View>
        )}

        {errorMessage && (
          <Text style={{ color: colors.red.main }} className="text-xs mt-3">
            {errorMessage}
          </Text>
        )}
      </View>
    </Animated.View>
  );
}
